import {
  FilterType,
  SoDataTableColumnType,
  SoDataTableCustomComponent,
  SoDataTableDatePeriod,
  SoDataTableFilters,
  SoDataTableHeader,
} from "@/types/components/SoDataTable"
import { BillRequestTypeCode } from "@/models/billRequest/billRequest.types"
import { SoChipType } from "@/types/components/SoChip"
import { hasOnly } from "@/helpers/arrayHelpers"
import { Dayjs } from "dayjs"

export enum TransactionFilterTypes {
  ACCOUNT_CREDIT = "Account Credit",
  BILL = "Bill",
  PAYMENT = "Payment",
  REPAYMENT = "Repayment",
}

export const transactionTypeIcons = {
  Bill: "file",
  Credit: "square-plus",
  "Account Credit": "square-plus",
  Repayment: "user-plus",
  Payment: "folder",
}

export const tableFilters: SoDataTableFilters = {
  transactionType: {
    label: "Transaction type",
    type: FilterType.CHECKBOX,
    value: [],
    options: {
      [TransactionFilterTypes.ACCOUNT_CREDIT]: "Account Credits",
      [TransactionFilterTypes.BILL]: "Bill",
      [TransactionFilterTypes.PAYMENT]: "Payments",
      [TransactionFilterTypes.REPAYMENT]: "Refunds",
    },
    filter: (filters, item) => {
      const filter = filters.transactionType
      if (filter.type !== FilterType.CHECKBOX) return true
      if (!filter.value?.length) return true
      return filter.value.includes(String(item.type))
    },
    expandable: true,
  },
}

interface ConditionalTableHeader extends SoDataTableHeader {
  showIf?: (transactionTypes: Array<string | number>) => boolean
}

export const transactionHeaders: ConditionalTableHeader[] = [
  {
    key: "billNumber",
    title: "Bill Number",
    showIf: (transactionTypes) =>
      hasOnly(transactionTypes, TransactionFilterTypes.BILL),
  },
  {
    key: "date",
    title: "Date",
    type: SoDataTableColumnType.DATE,
  },
  {
    key: "title",
    title: "Type",
    fallbackValue: "",
    showIf: (transactionTypes) => transactionTypes.length !== 1,
  },
  {
    key: "description",
    title: "Description",
    fallbackValue: "",
    showIf: (transactionTypes) =>
      (!!transactionTypes.length &&
        transactionTypes.every((filter) => {
          return (
            filter === TransactionFilterTypes.ACCOUNT_CREDIT ||
            filter === TransactionFilterTypes.PAYMENT
          )
        })) ||
      transactionTypes.length === 0,
  },
  {
    key: "billPeriod",
    title: "From - To",
    type: SoDataTableColumnType.DATE_PERIOD,
    showIf: (transactionTypes) =>
      hasOnly(transactionTypes, TransactionFilterTypes.BILL),
  },
  {
    key: "charge",
    title: "Charge",
    type: SoDataTableColumnType.MONEY,
    fallbackValue: "",
    showIf: (transactionTypes) =>
      hasOnly(transactionTypes, TransactionFilterTypes.BILL),
  },
  {
    key: "creditDebit",
    title: "Credit / Debit",
    type: SoDataTableColumnType.MONEY_COLORED,
    fallbackValue: "",
    showIf: (transactionTypes) => transactionTypes.length === 0,
  },
  {
    key: "credit",
    title: "Credit",
    type: SoDataTableColumnType.MONEY,
    fallbackValue: "",
    showIf: (transactionTypes) =>
      !transactionTypes.includes(TransactionFilterTypes.BILL) &&
      !!transactionTypes.length,
  },
  {
    key: "balance",
    title: "Balance",
    type: SoDataTableColumnType.MONEY_COLORED,
    fallbackValue: "",
  },
  {
    key: "billType",
    title: "Type",
    type: SoDataTableColumnType.CHIP,
    fallbackValue: "",
    showIf: (transactionTypes) =>
      hasOnly(transactionTypes, TransactionFilterTypes.BILL),
  },
  {
    key: "status",
    title: "Status",
    type: SoDataTableColumnType.STATUS,
    fallbackValue: "",
  },
]

export const billTypeStatuses: { [key in BillRequestTypeCode]: SoChipType } = {
  Normal: SoChipType.SUCCESS,
  Hot: SoChipType.ERROR,
  NewVersion: SoChipType.IMPORTANT,
}

export type TransactionTableItem = {
  isFailed: boolean
  type: string
  title: string
  date: Dayjs
  icon: string
  status: SoDataTableCustomComponent
  description?: string
  transactionId?: number
  balance?: number
  billNumber?: string
  billId?: number
  billPeriod?: SoDataTableDatePeriod
  charge?: number
  billType?: SoDataTableCustomComponent
  debit?: number
  credit?: number
  accountCreditId?: string
}
